import { observable, computed, makeObservable, runInAction, action } from 'mobx';
import { Server, LoadBalancer, Vpc, DomainOverview } from '../ApiModel';
import { IApiClient } from '../ApiClient';
import { ISiteServicesApiClient } from '../SiteServicesApiClient';
import { any, hasItem, orderBy } from '../helper/ArrayHelper';
import { loadAllItems } from '../helper/PagingHelper';
import { serverRoute, loadBalancerRoute, networkRoute, domainRoute } from '../helper/RouteHelper';
import { LoadingTracker } from './LoadingTracker';
import { getAppContext } from '../AppContext';

type Resources = {
    servers: Server[];
    loadBalancers: LoadBalancer[];
    networks: Vpc[];
    domains: DomainOverview[];
};

export function substringMatches(query: string, term: string): boolean {
    return term.toLowerCase().includes(query.toLowerCase());
}

export function apiIdMatches(query: string, apiId: number): boolean {
    // match chars + 6 digit SID eg au100000 or integer apiId
    const match = query.toLowerCase().match(/^[a-z]*(\d{6})$|^(\d+)$/);
    if (!match) return false;

    const queryId = parseInt(match[1] ?? match[2] ?? '', 10);
    return queryId === apiId;
}

export function ipMatches(query: string, ip: string): boolean {
    // if it doesn't look like an IP address, then bail early
    // this avoids matching when people are searching for id.
    if (!query.includes('.') && !query.includes(':')) {
        return false;
    }
    return substringMatches(query, ip);
}

export class SearchStore {
    @observable
    resources: Resources = {
        servers: [],
        loadBalancers: [],
        networks: [],
        domains: [],
    };

    @observable
    searchQuery = '';

    @action
    setSearchQuery(query: string) {
        this.searchQuery = query;
    }

    loader = new LoadingTracker();

    private abortController?: AbortController;

    @computed
    get filteredResources(): Resources {
        if (this.searchQuery.length === 0) {
            return { ...this.resources };
        }

        const query = this.searchQuery;

        return {
            servers: this.resources.servers.filter((result) =>
                any([
                    apiIdMatches(query, result.id),
                    substringMatches(query, result.name),
                    result.image.full_name && substringMatches(query, result.image.full_name),
                    substringMatches(query, result.region.name),
                    substringMatches(query, result.region.slug),
                    any(result.networks.v4.map((network) => ipMatches(query, network.ip_address))),
                    any(result.networks.v6.map((network) => ipMatches(query, network.ip_address))),
                    any(
                        result.networks.v4.map((network) =>
                            network.nat_target ? ipMatches(query, network.nat_target) : false
                        )
                    ),
                ])
            ),
            loadBalancers: this.resources.loadBalancers.filter((result) =>
                any([apiIdMatches(query, result.id), substringMatches(query, result.name), ipMatches(query, result.ip)])
            ),
            networks: this.resources.networks.filter((result) =>
                any([
                    apiIdMatches(query, result.id),
                    substringMatches(query, result.name),
                    ipMatches(query, result.ip_range),
                ])
            ),
            domains: this.resources.domains.filter((result) => any([substringMatches(query, result.domain_name)])),
        };
    }

    getFirstResultUrl(): string | undefined {
        if (hasItem(this.filteredResources.servers)) {
            return serverRoute(this.filteredResources.servers[0]);
        }
        if (hasItem(this.filteredResources.loadBalancers)) {
            return loadBalancerRoute(this.filteredResources.loadBalancers[0]);
        }
        if (hasItem(this.filteredResources.networks)) {
            return networkRoute(this.filteredResources.networks[0]);
        }
        if (hasItem(this.filteredResources.domains)) {
            return domainRoute(this.filteredResources.domains[0].domain_name);
        }
        return undefined;
    }

    /**
     * Abort any outstanding loadResources() requests (e.g. when the search modal closes).
     *
     * The store does not self-cancel on disposal: the owner must call this from its teardown
     * path (SearchModal does so in componentWillUnmount), otherwise in-flight requests leak
     * once the consumer goes away.
     */
    cancel() {
        this.abortController?.abort();
    }

    async loadResources() {
        const { apiClient, siteApiClient } = this;

        // Abort any in-flight load from a previous call, then start a fresh controller.
        this.cancel();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        // Each loader writes its own slice of `resources` as it resolves. If a load is aborted after
        // some slices have already landed, those results are intentionally left in place (last-write-wins):
        // the next loadResources() overwrites them, which is preferable to blanking the modal on reopen.
        const serverLoader = loadAllItems((pageSpec) => apiClient.getServers(pageSpec, signal)).then((servers) => {
            runInAction(() => {
                this.resources.servers = orderBy(servers, (server) => server.name);
            });
        });

        const loadBalancerLoader = loadAllItems((pageSpec) => apiClient.getLoadBalancers(pageSpec, signal)).then(
            (loadBalancers) => {
                runInAction(() => {
                    this.resources.loadBalancers = orderBy(loadBalancers, (lb) => lb.name);
                });
            }
        );

        const networkLoader = loadAllItems((pageSpec) => apiClient.getVPCs(pageSpec, signal)).then((networks) => {
            runInAction(() => {
                this.resources.networks = orderBy(networks, (network) => network.name);
            });
        });

        const domainLoader = loadAllItems((pageSpec) => siteApiClient.getManagedDomains(pageSpec, signal)).then(
            (domains) => {
                runInAction(() => {
                    this.resources.domains = orderBy(domains, (domain) => domain.domain_name);
                });
            }
        );

        // wait for all the loaders to finish
        try {
            await this.loader.track(() => Promise.all([serverLoader, loadBalancerLoader, networkLoader, domainLoader]));
        } catch (e) {
            // If this load was cancelled (modal closed, or a newer search superseded it) its results are
            // no longer wanted, so suppress any error regardless of shape — not just the abort itself. This
            // also covers the race where a genuine failure and an abort reject together and Promise.all
            // happens to surface the genuine one.
            if (signal.aborted) return;
            // The search modal loads its resources once per open; on a genuine failure the global error
            // modal is the intended surface, so there is no inline error display here.
            getAppContext().error(e, { errorSummary: 'Unable to load search results' });
        }
    }

    constructor(
        // biome-ignore lint/correctness/noUnusedPrivateClassMembers: accessed via destructuring in loadResources
        private apiClient: IApiClient,
        // biome-ignore lint/correctness/noUnusedPrivateClassMembers: accessed via destructuring in loadResources
        private siteApiClient: ISiteServicesApiClient
    ) {
        makeObservable(this);
    }
}
